import { Board } from "../board";
import { Direction } from "../direction";
import { MusicId } from "../musicId";
import type { PhysicalThing } from "../physicalThing";
import { Strings } from "../strings";
import { XY } from "../xy";
// BlackFloor used as board.defaultFloor string reference
import { GrayStoneFloor2 } from "../things/floors/grayStoneFloor2";
import { GrayStoneFloor1 } from "../things/floors/grayStoneFloor1";
import { GraySlab } from "../things/floors/graySlab";
import { ConcreteFloor } from "../things/floors/concreteFloor";
import { WarningFloor } from "../things/floors/warningFloor";
import { BrownFloor } from "../things/floors/brownFloor";
import { MetalBlock } from "../things/metalBlock";
import { MetalBlockDiagonal } from "../things/metalBlockDiagonal";
import { ConcreteBlock } from "../things/concreteBlock";
import { Downstairs } from "../things/downstairs";
import { Upstairs } from "../things/upstairs";
import { PottedPlant } from "../things/pottedPlant";
import { MachineRack } from "../things/machineRack";
import { MachineConnectedToWall } from "../things/machineConnectedToWall";
import { WoodenDoor } from "../things/woodenDoor";
import { BlastDoor } from "../things/blastDoor";
import { OpenBlastDoorsTerminal } from "../things/openBlastDoorsTerminal";
import { OfficeChair } from "../things/officeChair";
import { Text } from "../things/text";
import { ArrowDisplay } from "../things/tutorial/arrowDisplay";
import { TimedAction } from "../things/tutorial/timedAction";
import { PlayerReachableAction } from "../things/tutorial/playerReachableAction";
import { NoMobsInAreaAction } from "../things/tutorial/noMobsInAreaAction";
import { FloatingArrow } from "../things/tutorial/floatingArrow";
import { PlayerOnTileAction } from "../things/tutorial/playerOnTileAction";
import { PlayerOutOfAmmoAction } from "../things/tutorial/playerOutOfAmmoAction";
import { PlayerHasAmmoAction } from "../things/tutorial/playerHasAmmoAction";
import { ThingDestroyedAction } from "../things/tutorial/thingDestroyedAction";
import { NoThreatsAction } from "../things/tutorial/noThreatsAction";
import { LootBox } from "../things/lootBox";
import { MetalLootBox } from "../things/metalLootBox";
import { KeyLootBox } from "../things/keyLootBox";
import { MetalDoor } from "../things/metalDoor";
import { Cannon } from "../weapons/cannon";
import { MissileGun } from "../weapons/missileGun";
import { Ammo } from "../items/ammo";
import { Key } from "../items/key";
import { Squid } from "../mobs/squid";
import { EmptyBarrel } from "../things/emptyBarrel";
import { WoodenShelf } from "../things/woodenShelf";
import { PlasticCabinet } from "../things/plasticCabinet";
import { WoodenDesk } from "../things/woodenDesk";
import { Plant } from "../things/plant";
import { Rock } from "../things/rock";
import { GasBarrel } from "../things/gasBarrel";
import { ExplosiveBarrel } from "../things/explosiveBarrel";
import { TerminalState } from "../things/terminal";
import { Portal2 } from "../things/portal2/portal2";
import { Portal2Part } from "../things/portal2/portal2Part";
import { ReinforcedPortal2 } from "../things/portal2/reinforcedPortal2";

import { InvulnerableBigContainer } from "../things/tutorial/invulnerableBigContainer";
import { MultiTileThingController } from "../things/multitile/multiTileThingController";
import { MovementEffect } from "../effects/movementEffect";
import type { Weapon } from "../weapon";
import { Prefab } from "./prefab";
import { SimpleBoard } from "./simpleBoard";
import { drawText, drawMessage } from "./levelGeneratorUtils";

const BOARD_WIDTH = 50;
const BOARD_HEIGHT = 28;
const MAXY = BOARD_HEIGHT - 1;

function flipBoard(board: Board): void {
	const things = [...board.getPhysicalThings()];
	for (const thing of things) {
		board.removeThing(thing);
		thing.active = true;
		thing.xy = new XY(thing.xy.x, MAXY - thing.xy.y);
		board.addThing(thing);
	}
}

// Colors used across tutorial levels (COLOR_YELLOW and COLOR_BLACK used in later levels)
export const COLOR_YELLOW = "#eee033";
export const COLOR_WHITE = "#FFFFFF";
export const COLOR_WHITE_FADED = "#DDDDDD";
export const COLOR_BLACK = "#000000";

export function displayKey(board: Board, center: XY, d: Direction, key: string): void {
	board.addThing(new ArrowDisplay(center.addDirection(d), d));
	drawText(board, center.addDirection(d).addDirection(d), key, COLOR_WHITE);
}

export function getExit(board: Board): XY {
	const downstairs = board.getPhysicalThings().find((t) => t instanceof Downstairs);
	if (!downstairs) throw new Error("No downstairs");
	return downstairs.xy;
}

export function locate<T extends PhysicalThing>(board: Board, clazz: new (...args: any[]) => T): XY {
	return locateThing(board, clazz).xy;
}

export function locateThing<T extends PhysicalThing>(board: Board, clazz: new (...args: any[]) => T): PhysicalThing {
	const thing = board.getPhysicalThings().find((t) => t instanceof clazz);
	if (!thing) throw new Error("No thing of class");
	return thing;
}

export function drawKeys(board: Board, center: XY): void {
	displayKey(board, center, Direction.N, "W");
	displayKey(board, center, Direction.W, "A");
	displayKey(board, center, Direction.E, "D");
	displayKey(board, center, Direction.S, "S");

	displayKey(board, center, Direction.NW, "Q");
	displayKey(board, center, Direction.NE, "E");
	displayKey(board, center, Direction.SW, "Z");
	displayKey(board, center, Direction.SE, "C");
}

export function eraseTexts(board: Board, area: XY[]): void {
	const areaSet = new Set(area.map((xy) => xy.toString()));
	const toRemove = board
		.getPhysicalThings()
		.filter((t) => t instanceof Text)
		.filter((t) => areaSet.has(t.xy.toString()));
	for (const t of toRemove) {
		board.removeAtEndOfTurn(t);
	}
}

export class TutorialLevelGenerator {
	generateNewLevel(levelNumber: number): Board {
		if (levelNumber === 0) return getLevel1();
		if (levelNumber === 1) return getLevel2();
		if (levelNumber === 2) return getLevel3();
		if (levelNumber === 3) return getLevel4();
		throw new Error("Invalid tutorial level number");
	}
}

function getLevel1(): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
	board.areaName = Strings.get("area_tutorial_1") ?? "Tutorial Level 1";
	board.musicId = MusicId.WASTELAND;

	board.defaultFloor = "BlackFloor";

	const prefabRoot = new XY(10, 14);

	getLevel1Prefab().addToBoard(board, prefabRoot);

	drawMessage(board, new XY(4, 12), "tutorial_1_1", COLOR_WHITE_FADED);
	drawMessage(board, new XY(4, 10), "tutorial_1_2", COLOR_WHITE_FADED);

	board.addNonPhysicalThing(
		new TimedAction(5, () => {
			drawMessage(board, getExit(board).add(4, 0), "tutorial_1_4", COLOR_WHITE);
		}),
	);

	flipBoard(board);

	drawKeys(board, new XY(25, MAXY - 10));

	const flippedRoot = new XY(prefabRoot.x, MAXY - prefabRoot.y);
	board.initialSpawnXY = flippedRoot.add(2, -3);
	return board;
}

function getLevel2(): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
	board.areaName = Strings.get("area_tutorial_2") ?? "Tutorial Level 2";
	board.musicId = MusicId.WASTELAND;

	board.defaultFloor = "BlackFloor";
	const prefabRoot = new XY(11, 4);
	getLevel2Prefab().addToBoard(board, prefabRoot);

	// PlayerReachableAction extends PhysicalThing — placed before flipBoard so it gets flipped.
	// Use Java-convention south (0, -1) since this is a pre-flip position.
	const blastDoorXY = locate(board, BlastDoor);
	board.addThing(
		new PlayerReachableAction(blastDoorXY.add(0, -1), () => {
			// At runtime the board is flipped — locate() returns TS-convention positions
			const bxyRT = locate(board, OpenBlastDoorsTerminal);
			const message2Area = [
				...bxyRT.add(-10, -10).getAreaTo(bxyRT.add(-3, 0)),
				...bxyRT.add(-10, -10).getAreaTo(bxyRT.add(board.width - 1, -8)),
			];
			eraseTexts(board, message2Area);

			const message3xy = locate(board, BlastDoor).add(-4, 5);
			drawMessage(board, message3xy.add(-8, 0), "tutorial_2_4", COLOR_WHITE);
			drawMessage(board, message3xy.add(-8, 2), "tutorial_2_5", COLOR_WHITE_FADED);
			drawMessage(board, message3xy.add(-8, 4), "tutorial_2_6", COLOR_WHITE_FADED);
			drawKeys(board, message3xy.add(23, 3));
		}),
	);

	board.addNonPhysicalThing(
		new TimedAction(5, () => {
			const message1Start = locate(board, BlastDoor).add(5, 0);
			drawMessage(board, message1Start, "tutorial_2_1", COLOR_WHITE_FADED);
		}),
	);

	board.addNonPhysicalThing(
		new TimedAction(9, () => {
			const bxyRT = locate(board, OpenBlastDoorsTerminal);
			drawMessage(board, bxyRT.add(-10, -8), "tutorial_2_2", COLOR_WHITE);
			drawMessage(board, bxyRT.add(-10, -6), "tutorial_2_3", COLOR_WHITE_FADED);

			const message1Start = locate(board, BlastDoor).add(5, 0);
			eraseTexts(board, message1Start.getAreaTo(message1Start.add(30, 0)));
		}),
	);

	flipBoard(board);

	return board;
}

function getLevel1Prefab(): Prefab {
	const board = new SimpleBoard();

	// Bottom wall
	for (const x of [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 19));
	}
	// Right wall
	for (const y of [12, 13, 14, 15, 16, 17, 18, 19]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(19, y));
	}
	// Left wall lower
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, 19));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, 18));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(4, 18));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(5, 18));
	// Left wall
	for (const y of [13, 14, 15, 16, 17]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(4, y));
	}
	// Top left wall
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(5, 13));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, 13));
	// Top wall
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, 12));
	for (let x = 7; x <= 19; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 12));
	}

	// GrayStoneFloor2 filling the room
	for (let x = 7; x <= 18; x++) {
		for (let y = 13; y <= 18; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// Extra floors on left wing
	for (let x = 5; x <= 6; x++) {
		for (let y = 14; y <= 16; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(5, 17));
	board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(6, 17));

	// Downstairs
	board.putThingAt(new Downstairs(new XY(0, 0), 1), new XY(17, 16));

	// Decorations
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(18, 18));
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(18, 13));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(9, 18));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(10, 18));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(8, 18));

	board.trim();
	return new Prefab(board);
}

function getLevel2Prefab(): Prefab {
	const board = new SimpleBoard();

	// Upper corridor walls
	for (const x of [20, 21, 22, 23, 24, 25, 26, 27, 28]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 25));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(28, 24));
	for (const x of [29, 30, 31]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 24));
	}
	for (const y of [20, 21, 22, 23]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(31, y));
	}
	for (const x of [28, 29, 30]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 20));
	}
	for (const y of [18, 19]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(28, y));
	}
	for (const x of [18, 19, 20, 21, 22, 23, 24, 25, 26, 27]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 18));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 19));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 21));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 22));
	for (const x of [19, 20, 21, 22]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 22));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(20, 23));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(20, 24));

	// Corridor walls
	for (const x of [14, 15, 16, 17]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 19));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(13, 19));
	for (const x of [13, 14, 15, 16, 17]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 21));
	}
	for (const y of [16, 17, 18, 19, 21, 22, 23, 24, 25]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(13, y));
	}

	// Left room walls
	for (const x of [6, 7, 8, 9, 10, 11, 12]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 25));
	}
	for (const y of [16, 17, 18, 19, 20, 21, 22, 23, 24]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, y));
	}
	for (const x of [7, 8, 9, 11, 12]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 16));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(8, 15));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(8, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(11, 15));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(11, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(8, 13));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(9, 12));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(10, 11));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(11, 10));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(12, 9));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(13, 9));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(14, 9));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, 9));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(14, 11));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, 11));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 11));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 9));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(12, 13));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(13, 12));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 12));

	// Right room lower area
	for (const x of [16, 17, 18, 19, 20, 21]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 8));
	}
	for (const x of [17, 18, 19, 20, 21]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 12));
	}
	for (const y of [8, 9, 10, 11, 12]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(21, y));
	}

	// Gray slab ramps
	for (const [x, y] of [
		[10, 13],
		[11, 12],
		[12, 11],
		[13, 10],
		[14, 10],
		[15, 10],
		[16, 10],
		[10, 14],
		[9, 14],
		[9, 15],
		[10, 15],
		[10, 16],
	] as [number, number][]) {
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(x, y));
	}

	// MetalBlockDiagonal NE going up
	for (const [x, y] of [
		[9, 13],
		[10, 12],
		[11, 11],
		[12, 10],
	] as [number, number][]) {
		board.putThingAt(new MetalBlockDiagonal(new XY(0, 0), Direction.NE), new XY(x, y));
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(x, y));
	}

	// MetalBlockDiagonal SW going down
	for (const [x, y] of [
		[11, 13],
		[12, 12],
		[13, 11],
	] as [number, number][]) {
		board.putThingAt(new MetalBlockDiagonal(new XY(0, 0), Direction.SW), new XY(x, y));
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(x, y));
	}

	// GrayStoneFloor1 for upper rooms
	for (let x = 22; x <= 23; x++) {
		for (const y of [23, 24]) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}
	for (let x = 24; x <= 26; x++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, 23));
	}
	for (let x = 23; x <= 25; x++) {
		for (let y = 19; y <= 22; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}
	for (let x = 28; x <= 30; x++) {
		for (let y = 21; y <= 23; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}
	for (const [x, y] of [
		[26, 21],
		[27, 21],
		[26, 22],
		[27, 22],
		[27, 23],
		[27, 24],
		[27, 20],
		[26, 20],
		[27, 19],
		[26, 19],
	] as [number, number][]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
	}
	for (let x = 19; x <= 22; x++) {
		for (let y = 19; y <= 21; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}

	// Left room GrayStoneFloor1
	for (const [x, y] of [
		[7, 24],
		[8, 24],
		[9, 24],
		[7, 23],
		[8, 23],
		[9, 23],
		[8, 22],
		[9, 22],
		[8, 21],
		[9, 21],
		[7, 20],
		[8, 20],
		[9, 20],
		[8, 19],
		[9, 19],
		[8, 18],
		[9, 18],
		[7, 17],
		[8, 17],
		[9, 17],
	] as [number, number][]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
	}
	for (let x = 10; x <= 12; x++) {
		for (let y = 17; y <= 24; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}
	for (const [x, y] of [
		[7, 22],
		[7, 21],
		[7, 19],
		[7, 18],
	] as [number, number][]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
	}

	// Corridor GrayStoneFloor2
	for (let x = 13; x <= 18; x++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, 20));
	}

	// Lower room GrayStoneFloor2
	for (let x = 17; x <= 20; x++) {
		for (let y = 9; y <= 11; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}

	// Additional upper floor tiles
	for (const [x, y] of [
		[24, 24],
		[25, 24],
		[26, 24],
		[21, 24],
		[21, 23],
	] as [number, number][]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
	}

	// Doors
	board.putThingAt(new WoodenDoor(new XY(0, 0)), new XY(18, 20));
	board.putThingAt(new WoodenDoor(new XY(0, 0)), new XY(13, 20));
	board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(10, 16));
	board.putThingAt(new WoodenDoor(new XY(0, 0)), new XY(16, 10));

	// Furniture
	board.putThingAt(new MachineConnectedToWall(new XY(0, 0)), new XY(8, 24));
	board.putThingAt(new MachineConnectedToWall(new XY(0, 0)), new XY(9, 24));
	board.putThingAt(new MachineConnectedToWall(new XY(0, 0)), new XY(10, 24));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(21, 23));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(21, 24));

	// Level transitions
	board.putThingAt(new Upstairs(new XY(0, 0), 0), new XY(29, 22));
	board.putThingAt(new Downstairs(new XY(0, 0), 2), new XY(19, 10));

	// Decorations
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(27, 24));
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(27, 19));
	board.putThingAt(new OpenBlastDoorsTerminal(new XY(0, 0)), new XY(8, 21));
	board.putThingAt(new OfficeChair(new XY(0, 0)), new XY(8, 20));

	board.trim();
	return new Prefab(board);
}

// --- Helper functions for Levels 3 and 4 ---

function ammoReplacementTrigger(
	board: Board,
	replacementXY: XY,
	weaponClass: new (...args: any[]) => Weapon,
	ammoSupplier: () => Ammo,
	getAmmoAction: (() => void) | null,
	runOutAction: (() => void) | null,
): void {
	board.addNonPhysicalThing(
		new PlayerOutOfAmmoAction(weaponClass, () => {
			const lootBox = new LootBox(replacementXY, [ammoSupplier()]);
			board.addThing(lootBox);
			const dropOrigin = replacementXY.add(new XY(0, -2));
			board.addEffect(new MovementEffect(lootBox, dropOrigin, replacementXY));
			board.addThing(new FloatingArrow(replacementXY));

			if (runOutAction) runOutAction();

			board.addThing(
				new PlayerOnTileAction(replacementXY, () => {
					const arrow = board.getThingOfClass(FloatingArrow, replacementXY);
					if (arrow) board.removeAtEndOfTurn(arrow);
					if (getAmmoAction) getAmmoAction();
					ammoReplacementTrigger(board, replacementXY, weaponClass, ammoSupplier, getAmmoAction, runOutAction);
				}),
			);
		}),
	);
}

function createCannonAmmo(amount: number): Ammo {
	return new Ammo("Cannon", amount);
}

function createMissileGunAmmo(amount: number): Ammo {
	return new Ammo("MissileGun", amount);
}

// --- Level 3 ---

function getLevel3(): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
	board.areaName = Strings.get("area_tutorial_3") ?? "Tutorial Level 3";
	board.musicId = MusicId.WASTELAND;

	board.defaultFloor = "BlackFloor";
	const prefabRoot = new XY(0, 3);
	getLevel3Prefab().addToBoard(board, prefabRoot);

	const solidifierXY = prefabRoot.add(29, 1);
	const cannonXY = prefabRoot.add(21, 18);

	drawMessage(board, prefabRoot.add(24, -2), "tutorial_3_1", COLOR_WHITE);

	// PlayerOnTileAction extends PhysicalThing — placed before flipBoard so it gets flipped
	board.addThing(
		new PlayerOnTileAction(solidifierXY, () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			eraseTexts(board, board.getAllXYs());
			drawMessage(board, fr.add(29, -18), "tutorial_3_2", COLOR_WHITE);

			board.addNonPhysicalThing(
				new TimedAction(2, () => {
					drawMessage(board, fr.add(29, -16), "tutorial_3_3", COLOR_WHITE_FADED);
				}),
			);

			board.addNonPhysicalThing(
				new TimedAction(5, () => {
					drawMessage(board, fr.add(29, -14), "tutorial_3_5", COLOR_WHITE_FADED);
				}),
			);

			board.addNonPhysicalThing(
				new TimedAction(10, () => {
					drawMessage(board, fr.add(29, -12), "tutorial_3_7", COLOR_WHITE_FADED);
				}),
			);

			board
				.getPhysicalThings()
				.filter((t) => t instanceof OpenBlastDoorsTerminal)
				.forEach((t) => (t as OpenBlastDoorsTerminal).setState(TerminalState.ACTIVE));
		}),
	);

	// NoMobsInAreaAction extends PhysicalThing — placed before flipBoard so it gets flipped
	board.addThing(
		new NoMobsInAreaAction(locate(board, Squid), () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			const flippedCannonXY = new XY(prefabRoot.x + 21, MAXY - (prefabRoot.y + 18));
			board
				.getPhysicalThings()
				.filter((t) => t instanceof BlastDoor)
				.forEach((t) => (t as BlastDoor).open());

			eraseTexts(board, board.getAllXYs());

			board.addThing(new FloatingArrow(flippedCannonXY));

			drawMessage(board, fr.add(29, -18), "tutorial_3_9", COLOR_WHITE);
		}),
	);

	// PlayerOnTileAction extends PhysicalThing — placed before flipBoard so it gets flipped
	board.addThing(
		new PlayerOnTileAction(cannonXY, () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			const flippedCannonXY = new XY(prefabRoot.x + 21, MAXY - (prefabRoot.y + 18));
			const arrow = board.getThingOfClass(FloatingArrow, flippedCannonXY);
			if (arrow) board.removeAtEndOfTurn(arrow);

			drawMessage(board, fr.add(29, -16), "tutorial_3_10", COLOR_WHITE_FADED);

			board.addNonPhysicalThing(
				new TimedAction(2, () => {
					drawMessage(board, fr.add(29, -14), "tutorial_3_11", COLOR_WHITE_FADED);
				}),
			);

			ammoReplacementTrigger(
				board,
				flippedCannonXY,
				Cannon,
				() => createCannonAmmo(10),
				() => {
					eraseTexts(board, board.getAllXYs());
					drawMessage(board, fr.add(29, -18), "tutorial_3_10", COLOR_WHITE_FADED);
				},
				() => {
					eraseTexts(board, board.getAllXYs());
					drawMessage(board, fr.add(29, -16), "tutorial_3_14", COLOR_WHITE_FADED);
				},
			);
		}),
	);

	// FloatingArrow + PlayerOnTileAction extend PhysicalThing — placed before flipBoard
	board.addThing(new FloatingArrow(solidifierXY));
	board.addThing(
		new PlayerOnTileAction(solidifierXY, () => {
			const flippedSolidifierXY = new XY(prefabRoot.x + 29, MAXY - (prefabRoot.y + 1));
			const arrow = board.getThingOfClass(FloatingArrow, flippedSolidifierXY);
			if (arrow) board.removeAtEndOfTurn(arrow);
		}),
	);

	flipBoard(board);

	const entry = locate(board, Upstairs);
	board.initialSpawnXY = entry;

	return board;
}

function getLevel3Prefab(): Prefab {
	const board = new SimpleBoard();

	// === MetalBlock walls ===

	// Top wall (y=4, x=4-33)
	for (let x = 4; x <= 33; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 4));
	}

	// Entrance room (upper right, around Upstairs at 34,8)
	for (const x of [33, 34, 35, 36]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 6));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 10));
	}
	for (const y of [7, 8, 9]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(33, 5));
	for (const y of [11, 12]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(33, y));
	}

	// Combat room walls
	for (let x = 26; x <= 32; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 12));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 5));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, 5));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 8));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 11));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, 11));

	// Far-left passage (x=2-4)
	for (let y = 6; y <= 13; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(2, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(3, 5));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(3, 6));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(4, 5));

	// South border of slab area
	for (const x of [3, 4, 6, 7, 8, 9, 10]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 13));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(10, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(11, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 13));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, 13));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(25, 14));

	// Lower wall (y=15)
	for (const x of [11, 12, 13, 14, 15, 16, 17, 20, 21, 24, 25, 26, 27]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 15));
	}

	// Narrow passage (x=4 and x=6, y=14-18)
	for (const y of [14, 15, 16, 17, 18]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(4, y));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(6, y));
	}
	for (const x of [7, 8, 9]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 18));
	}

	// Barrel room walls
	for (const x of [1, 2, 3]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 18));
	}
	for (let y = 19; y <= 24; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(1, y));
	}
	for (let x = 2; x <= 9; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 24));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(9, 19));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(9, 23));

	// Lower corridor walls
	for (let x = 10; x <= 16; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 19));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 23));
	}
	for (const y of [16, 17, 18]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, y));
	}
	for (const y of [24, 25, 26]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, y));
	}

	// Cannon room walls
	for (let x = 16; x <= 27; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 26));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, 25));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(28, 25));
	for (let y = 16; y <= 24; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(28, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, 16));

	// === ConcreteBlock walls (left room) ===

	for (const x of [5, 6, 7, 8, 9, 10]) {
		board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(x, 5));
	}
	for (const x of [7, 8, 9]) {
		board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(x, 6));
	}
	for (const x of [7, 8, 9]) {
		board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(x, 10));
	}
	for (const x of [6, 7, 8, 9, 10]) {
		board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(x, 11));
	}
	for (const x of [3, 4, 5, 6, 7, 8, 9, 10]) {
		board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(x, 12));
	}
	board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(5, 13));
	board.putThingAt(new ConcreteBlock(new XY(0, 0)), new XY(5, 18));

	// === Blast doors ===

	// Combat room blast doors (doorId "combatRoom") — left side
	for (const y of [6, 7, 9, 10]) {
		board.putThingAt(new BlastDoor(new XY(0, 0), "combatRoom"), new XY(26, y));
	}
	// Left room blast doors (doorId "combatRoom")
	for (const y of [7, 8, 9]) {
		board.putThingAt(new BlastDoor(new XY(0, 0), "combatRoom"), new XY(8, y));
	}
	// Lower blast doors (no doorId)
	for (const x of [18, 19, 22, 23]) {
		board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(x, 15));
	}

	// === Floors ===

	// GrayStoneFloor1 under combat room blast doors
	for (const y of [6, 7, 9, 10]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(26, y));
	}
	// GrayStoneFloor1 under left room blast doors
	for (const y of [7, 8, 9]) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(8, y));
	}

	// GrayStoneFloor1 in combat room
	for (let x = 28; x <= 32; x++) {
		for (let y = 5; y <= 11; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}
	for (let y = 6; y <= 10; y++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(27, y));
	}
	for (let x = 33; x <= 35; x++) {
		for (let y = 7; y <= 9; y++) {
			board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(x, y));
		}
	}

	// GrayStoneFloor1 in left room
	for (let y = 7; y <= 11; y++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(3, y));
	}
	for (let y = 6; y <= 11; y++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(4, y));
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(5, y));
	}
	for (let y = 6; y <= 10; y++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(6, y));
	}
	for (let y = 7; y <= 9; y++) {
		board.putThingAt(new GrayStoneFloor1(new XY(0, 0)), new XY(7, y));
	}

	// WarningFloor at left room entrance
	for (const y of [7, 8, 9]) {
		board.putThingAt(new WarningFloor(new XY(0, 0)), new XY(9, y));
	}

	// GraySlab (large middle area)
	for (let y = 6; y <= 10; y++) {
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(10, y));
	}
	for (let y = 5; y <= 13; y++) {
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(11, y));
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(25, y));
	}
	for (let x = 12; x <= 24; x++) {
		for (let y = 5; y <= 14; y++) {
			board.putThingAt(new GraySlab(new XY(0, 0)), new XY(x, y));
		}
	}
	// GraySlab on lower blast door positions
	for (const x of [18, 19, 22, 23]) {
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(x, 15));
	}

	// ConcreteFloor in narrow passage
	for (let y = 14; y <= 17; y++) {
		board.putThingAt(new ConcreteFloor(new XY(0, 0)), new XY(5, y));
	}

	// GrayStoneFloor2 (lower rooms)
	for (let x = 2; x <= 8; x++) {
		for (let y = 19; y <= 23; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	for (let x = 9; x <= 15; x++) {
		for (let y = 20; y <= 22; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	for (const y of [16, 17, 18, 20, 21, 22, 24, 25]) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(16, y));
	}
	for (let x = 17; x <= 26; x++) {
		for (let y = 16; y <= 25; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	for (let y = 17; y <= 24; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(27, y));
	}

	// === Level transitions ===
	board.putThingAt(new Upstairs(new XY(0, 0), 1), new XY(34, 8));
	board.putThingAt(new Downstairs(new XY(0, 0), 3), new XY(3, 21));

	// === Terminal ===
	const terminal = new OpenBlastDoorsTerminal(new XY(0, 0), "combatRoom");
	terminal.setState(TerminalState.INACTIVE);
	board.putThingAt(terminal, new XY(27, 8));

	// === Mobs ===
	board.putThingAt(new Squid(new XY(0, 0)), new XY(5, 8));
	board.putThingAt(new Squid(new XY(0, 0)), new XY(5, 9));

	// === Loot ===
	board.putThingAt(new LootBox(new XY(0, 0), [new Ammo("Solidifier", 1)]), new XY(30, 5));
	board.putThingAt(new LootBox(new XY(0, 0), [createCannonAmmo(30)]), new XY(22, 22));

	// === Furniture ===
	// Barrels in barrel room
	for (const [x, y] of [
		[9, 20],
		[9, 22],
		[10, 20],
		[10, 21],
		[10, 22],
		[11, 20],
		[11, 21],
		[11, 22],
		[12, 20],
		[12, 21],
		[12, 22],
		[13, 20],
		[13, 21],
		[13, 22],
		[14, 22],
		[15, 22],
	] as [number, number][]) {
		board.putThingAt(new EmptyBarrel(new XY(0, 0)), new XY(x, y));
	}
	// Shelves in cannon room
	for (let x = 20; x <= 25; x++) {
		board.putThingAt(new WoodenShelf(new XY(0, 0)), new XY(x, 25));
	}
	// Potted plants
	for (const y of [16, 17, 18]) {
		board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(16, y));
	}
	// Cabinets, desks, chairs
	board.putThingAt(new PlasticCabinet(new XY(0, 0)), new XY(27, 18));
	board.putThingAt(new PlasticCabinet(new XY(0, 0)), new XY(27, 19));
	board.putThingAt(new WoodenDesk(new XY(0, 0)), new XY(27, 17));
	board.putThingAt(new WoodenDesk(new XY(0, 0)), new XY(27, 20));
	board.putThingAt(new OfficeChair(new XY(0, 0)), new XY(26, 17));
	board.putThingAt(new OfficeChair(new XY(0, 0)), new XY(26, 20));

	board.trim();
	return new Prefab(board);
}

// --- Level 4 ---

function getLevel4(): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
	board.areaName = Strings.get("area_tutorial_4") ?? "Tutorial Level 4";
	board.musicId = MusicId.WASTELAND;

	board.defaultFloor = "BlackFloor";
	const prefabRoot = new XY(0, 0);
	getLevel4Prefab().addToBoard(board, prefabRoot);

	// Place Portal2 on the real board (not in prefab, because it needs multi-tile setup)
	const portalXY = prefabRoot.add(35, 3);
	const portal = new Portal2(portalXY);
	board.addThing(portal);
	for (const xy of portalXY.getNeighbors()) {
		const part = new Portal2Part(xy);
		board.addThing(part);
		portal.addPart(part);
	}

	// Place ReinforcedPortal2
	const reinfPortalXY = prefabRoot.add(17, 3);
	const reinfPortal = new ReinforcedPortal2(reinfPortalXY);
	board.addThing(reinfPortal);
	for (const xy of reinfPortalXY.getNeighbors()) {
		const part = new Portal2Part(xy);
		board.addThing(part);
		reinfPortal.addPart(part);
	}

	// Place InvulnerableBigContainer
	const bigContainer = new InvulnerableBigContainer();
	bigContainer.addLoot(new MetalLootBox(new XY(0, 0), [createMissileGunAmmo(3)]));
	bigContainer.addLoot(new MetalLootBox(new XY(0, 0), [createMissileGunAmmo(3)]));
	const containerRootXY = prefabRoot.add(5, 25);
	MultiTileThingController.addToBoard(board, bigContainer, [
		containerRootXY,
		containerRootXY.add(1, 0),
		containerRootXY.add(0, 1),
		containerRootXY.add(1, 1),
	]);

	drawMessage(board, prefabRoot.add(1, 16), "tutorial_4_1", COLOR_WHITE);

	// ThingDestroyedAction tracks by thing reference — safe to call before flipBoard
	const metalDoorThing = locateThing(board, MetalDoor);
	const metalDoorPreFlipXY = metalDoorThing.xy;
	board.addNonPhysicalThing(
		new ThingDestroyedAction(metalDoorThing, () => {
			eraseTexts(board, board.getAllXYs());
			const arrow = locateThing(board, FloatingArrow);
			board.removeAtEndOfTurn(arrow);
		}),
	);

	board.addNonPhysicalThing(
		new PlayerHasAmmoAction(MissileGun, () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			const flippedMetalDoorXY = new XY(metalDoorPreFlipXY.x, MAXY - metalDoorPreFlipXY.y);
			eraseTexts(board, board.getAllXYs());
			drawMessage(board, fr.add(1, -16), "tutorial_4_3", COLOR_WHITE_FADED);
			drawMessage(board, fr.add(1, -14), "tutorial_4_4", COLOR_WHITE_FADED);

			board.addThing(new FloatingArrow(flippedMetalDoorXY));

			ammoReplacementTrigger(board, fr.add(5, -27), MissileGun, () => createMissileGunAmmo(6), null, null);
		}),
	);

	// PlayerOnTileAction extends PhysicalThing — placed before flipBoard so it gets flipped
	const metalDoorXY = metalDoorPreFlipXY;
	board.addThing(
		new PlayerOnTileAction(metalDoorXY.addDirection(Direction.E), () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			drawMessage(board, fr.add(1, -16), "tutorial_4_6", COLOR_WHITE_FADED);

			board.addNonPhysicalThing(
				new TimedAction(10, () => {
					drawMessage(board, fr.add(1, -14), "tutorial_4_8", COLOR_WHITE_FADED);
					drawMessage(board, fr.add(1, -12), "tutorial_4_9", COLOR_WHITE_FADED);
				}),
			);
		}),
	);

	// Configure portals
	portal.addConfig({ mobType: Squid.key, share: 1 });
	portal.setSpawnProbability(0);

	reinfPortal.addConfig({ mobType: Squid.key, share: 1 });
	reinfPortal.setSpawnProbability(0);

	// PlayerReachableAction extends PhysicalThing — placed before flipBoard so it gets flipped
	board.addThing(
		new PlayerReachableAction(prefabRoot.add(30, 4), () => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			ammoReplacementTrigger(board, fr.add(28, -23), Cannon, () => createCannonAmmo(6), null, null);
			portal.setSpawnProbability(0.8);
			reinfPortal.setSpawnProbability(0.8);

			board.addNonPhysicalThing(
				new TimedAction(2, () => {
					portal.setSpawnProbability(0.05);
					reinfPortal.setSpawnProbability(0.05);
				}),
			);
		}),
	);

	board.addNonPhysicalThing(
		new NoThreatsAction(() => {
			const fr = new XY(prefabRoot.x, MAXY - prefabRoot.y);
			eraseTexts(board, board.getAllXYs());

			drawMessage(board, fr.add(2, -14), "tutorial_4_10", COLOR_YELLOW);

			board.addNonPhysicalThing(
				new TimedAction(4, () => {
					drawMessage(board, fr.add(2, -12), "tutorial_4_11", COLOR_WHITE_FADED);
					drawMessage(board, fr.add(2, -10), "tutorial_4_12", COLOR_WHITE_FADED);
				}),
			);
		}),
	);

	flipBoard(board);

	const entry = locate(board, Upstairs);
	board.initialSpawnXY = entry;

	return board;
}

function getLevel4Prefab(): Prefab {
	const board = new SimpleBoard();

	// === MetalBlock walls ===

	// Arena top wall (y=5, x=15-37)
	for (let x = 15; x <= 37; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 5));
	}

	// Arena left wall
	for (let y = 6; y <= 14; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(14, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, 6));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(15, 15));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 15));

	// Arena right wall
	for (let y = 6; y <= 14; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(38, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(37, 6));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(37, 14));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(37, 15));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, 15));

	// Arena bottom wall — left section (y=16, x=16-25)
	for (let x = 16; x <= 25; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 16));
	}
	// Arena bottom wall — right section (y=16, x=32-36)
	for (let x = 32; x <= 36; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 16));
	}

	// Transition from arena to corridor (left side)
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(25, 17));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 17));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(26, 18));
	// Transition from arena to corridor (right side)
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(32, 17));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(31, 17));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(31, 18));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(30, 18));

	// Corridor walls (x=27 and x=30)
	for (let y = 18; y <= 21; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(27, y));
	}
	for (let y = 19; y <= 21; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(30, y));
	}

	// Diagonal wall — left (from arena down to entry corridor)
	for (let x = 22; x <= 27; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 21));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(21, 22));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(22, 22));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(21, 23));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(20, 23));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(20, 24));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(19, 24));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(19, 25));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 25));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 26));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(17, 26));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(17, 27));

	// Diagonal wall — right (from arena down to lower room)
	for (let x = 30; x <= 35; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 21));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(35, 22));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, 22));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, 23));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(37, 23));
	for (let y = 24; y <= 31; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(37, y));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, 31));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(36, 32));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(35, 32));

	// Bottom wall (y=33, x=18-35)
	for (let x = 18; x <= 35; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 32));
	}
	// Bottom-left connection
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(17, 31));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(17, 32));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(18, 32));

	// Entry room walls
	for (let x = 0; x <= 3; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 27));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 31));
	}
	for (let y = 28; y <= 30; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(0, y));
	}
	for (let y = 23; y <= 26; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(3, y));
	}
	for (let y = 32; y <= 34; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(3, y));
	}

	// Wider entry area walls
	for (let x = 4; x <= 10; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 23));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 32));
	}
	for (let y = 23; y <= 27; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(10, y));
	}
	for (let y = 31; y <= 34; y++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(10, y));
	}

	// Corridor walls (entry to metal door)
	for (let x = 11; x <= 14; x++) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 27));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 31));
	}
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 27));
	board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(16, 31));
	// Inner corridor walls (around door at 15,29)
	for (const x of [14, 15, 16]) {
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 28));
		board.putThingAt(new MetalBlock(new XY(0, 0)), new XY(x, 30));
	}

	// === BrownFloor (arena floor) ===

	// Main block: x=17-35, y=6-15
	for (let x = 17; x <= 35; x++) {
		for (let y = 6; y <= 15; y++) {
			board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(x, y));
		}
	}
	// Left edges
	for (let y = 7; y <= 13; y++) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(15, y));
	}
	for (let y = 6; y <= 14; y++) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(16, y));
	}
	// Right edges
	for (let y = 6; y <= 14; y++) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(36, y));
	}
	for (let y = 7; y <= 13; y++) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(37, y));
	}
	// South extension: x=26-31, y=16
	for (let x = 26; x <= 31; x++) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(x, 16));
	}
	// Corridor floor (y=17)
	for (const x of [27, 28, 29, 30]) {
		board.putThingAt(new BrownFloor(new XY(0, 0)), new XY(x, 17));
	}

	// === GrayStoneFloor2 (lower rooms) ===

	// Entry room: x=1-3, y=28-30
	for (let x = 1; x <= 3; x++) {
		for (let y = 28; y <= 30; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// Wider entry: x=4-9, y=24-33
	for (let x = 4; x <= 9; x++) {
		for (let y = 24; y <= 33; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// Corridor: x=10-13, y=28-30
	for (let x = 10; x <= 13; x++) {
		for (let y = 28; y <= 30; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// Door area: x=14-16, y=29
	for (let x = 14; x <= 16; x++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, 29));
	}
	// Expanding diagonal corridor
	for (let y = 28; y <= 30; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(17, y));
	}
	for (let y = 27; y <= 31; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(18, y));
	}
	for (let y = 26; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(19, y));
	}
	for (let y = 25; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(20, y));
	}
	for (let y = 24; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(21, y));
	}
	for (let y = 23; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(22, y));
	}
	// Lower room: x=23-26, y=22-32
	for (let x = 23; x <= 26; x++) {
		for (let y = 22; y <= 32; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// x=27, y=23-32
	for (let y = 23; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(27, y));
	}
	// x=28-29: y=18-21 and y=23-32
	for (const x of [28, 29]) {
		for (let y = 18; y <= 21; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
		for (let y = 23; y <= 32; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// x=30, y=23-32
	for (let y = 23; y <= 32; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(30, y));
	}
	// x=31-34, y=22-32
	for (let x = 31; x <= 34; x++) {
		for (let y = 22; y <= 32; y++) {
			board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(x, y));
		}
	}
	// x=35, y=23-31
	for (let y = 23; y <= 31; y++) {
		board.putThingAt(new GrayStoneFloor2(new XY(0, 0)), new XY(35, y));
	}

	// === WarningFloor ===
	for (const x of [27, 28, 29, 30]) {
		board.putThingAt(new WarningFloor(new XY(0, 0)), new XY(x, 22));
	}

	// === Blast doors ===
	board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(28, 21));
	board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(29, 21));
	board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(28, 18));
	board.putThingAt(new BlastDoor(new XY(0, 0)), new XY(29, 18));

	// === MetalDoor ===
	board.putThingAt(new MetalDoor(new XY(0, 0)), new XY(15, 29));

	// === Level transitions ===
	board.putThingAt(new Upstairs(new XY(0, 0), 2), new XY(2, 29));

	// === KeyLootBox ===
	board.putThingAt(new KeyLootBox(new XY(0, 0), [new Key(1)]), new XY(7, 25));

	// === Loot boxes ===
	for (const x of [27, 28, 29]) {
		board.putThingAt(new LootBox(new XY(0, 0), [createCannonAmmo(10)]), new XY(x, 29));
		board.putThingAt(new LootBox(new XY(0, 0), [createMissileGunAmmo(3)]), new XY(x, 27));
	}

	// === OpenBlastDoorsTerminal ===
	board.putThingAt(new OpenBlastDoorsTerminal(new XY(0, 0)), new XY(31, 22));

	// === Mobs ===
	board.putThingAt(new Squid(new XY(0, 0)), new XY(17, 11));
	board.putThingAt(new Squid(new XY(0, 0)), new XY(32, 10));

	// === Plants ===
	for (const [x, y] of [
		[16, 6],
		[17, 6],
		[18, 6],
		[15, 7],
		[17, 14],
		[16, 14],
		[15, 13],
		[17, 15],
		[18, 15],
		[19, 15],
		[20, 15],
		[34, 15],
		[35, 15],
		[36, 14],
		[26, 6],
		[25, 10],
		[26, 10],
		[27, 10],
	] as [number, number][]) {
		board.putThingAt(new Plant(new XY(0, 0)), new XY(x, y));
	}

	// === Rock ===
	for (const [x, y] of [
		[23, 6],
		[24, 6],
		[25, 6],
		[24, 7],
		[15, 12],
		[25, 11],
		[26, 11],
		[27, 11],
		[32, 11],
		[33, 11],
		[21, 15],
		[22, 15],
		[22, 14],
	] as [number, number][]) {
		board.putThingAt(new Rock(new XY(0, 0)), new XY(x, y));
	}

	// === Empty barrels ===
	for (let y = 24; y <= 30; y++) {
		board.putThingAt(new EmptyBarrel(new XY(0, 0)), new XY(36, y));
		board.putThingAt(new GraySlab(new XY(0, 0)), new XY(36, y));
	}
	for (const x of [23, 24, 25, 26]) {
		board.putThingAt(new EmptyBarrel(new XY(0, 0)), new XY(x, 23));
		board.putThingAt(new EmptyBarrel(new XY(0, 0)), new XY(x, 24));
	}

	// === Other furniture ===
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(19, 31));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(20, 31));
	board.putThingAt(new MachineRack(new XY(0, 0)), new XY(21, 31));
	board.putThingAt(new GasBarrel(new XY(0, 0)), new XY(33, 31));
	board.putThingAt(new GasBarrel(new XY(0, 0)), new XY(32, 31));
	board.putThingAt(new ExplosiveBarrel(new XY(0, 0)), new XY(25, 31));
	board.putThingAt(new ExplosiveBarrel(new XY(0, 0)), new XY(26, 31));
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(4, 24));
	board.putThingAt(new PottedPlant(new XY(0, 0)), new XY(4, 25));
	board.putThingAt(new WoodenShelf(new XY(0, 0)), new XY(9, 25));
	board.putThingAt(new WoodenShelf(new XY(0, 0)), new XY(9, 26));

	// Note: Portal2, ReinforcedPortal2 and InvulnerableBigContainer
	// are placed in getLevel4() on the real Board (multi-tile setup)

	board.trim();
	return new Prefab(board);
}
